import React, { useState, lazy, Suspense } from 'react';
import { NavLink, useLocation } from "react-router-dom";
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import jwt_decode from 'jwt-decode';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { ACCOUNT_ROUTE, AGENT_LIST, EMAIL_LIST, JOURNAL_GROUPS, STATS } from "../../../utils/consts/pathRoutes";
import { getUserRole, hasContingentAccess, hasJournalAccess } from "../../../utils/consts/roles";
import IconButton from "@mui/material/IconButton";
import PersonIcon from '@mui/icons-material/Person';
import { dropToken, getToken } from "../../../utils/token";
import SimCardDownloadRoundedIcon from '@mui/icons-material/SimCardDownloadRounded';
import Tooltip from '@mui/material/Tooltip';
import DownloadGeneratedFilesModal from '../../Modals/DownloadGeneratedFilesModal/DownloadGeneratedFilesModal';
import useIsMobile from '../../../utils/useIsMobile';
import './Navbar.css';

const ModalCustom = lazy(() => import('../../ModalCustom/ModalCustom'));

export default function Navbar() {
  const [isDownloadGeneratedFiles, setIsDownloadGeneratedFiles] = useState(false);
  const handleOpenDownloadGeneratedFiles = () => {
    setIsDownloadGeneratedFiles(prevState => !prevState);
  };

  let tokenName = '';
  let userName = '';
  const userRole = getUserRole();
  // Проверки положительные: раздел показывается роли, которая явно в списке.
  // Раньше было `!IS_TEACHER_ACCESS`, и любая новая роль получала весь контингент.
  const SHOW_CONTINGENT = hasContingentAccess(userRole);
  const SHOW_JOURNAL = hasJournalAccess(userRole);
  // Личный кабинет — у всех ролей, кроме старой «Преподаватель»: это одна общая учётка
  // на всех преподавателей, и смена пароля одним закрыла бы вход остальным.
  const IS_TEACHER_ACCESS = userRole === 'Преподаватель';
  try {
    if (getToken()) {
      // Имя показываем как есть. Раньше оно разбивалось по пробелам и склеивалось обратно
      // только для двух слов, а ФИО из трёх оставалось массивом — React печатал его
      // без пробелов: «ИвашоваНадеждаМихайловна».
      tokenName = jwt_decode(getToken()).name;
      userName = String(tokenName || '').trim() || 'Без имени';
    }
  } catch (e) {
    userName = "Не авторизовано";
  }

  const [isActive, setIsActive] = useState(false);
  const isMobile = useIsMobile();

  const path = useLocation().pathname;
  const IS_STUDENT_CARD = path.includes('contract') || path.includes('quota') || path.includes('network');

  // На узком экране ссылки на разделы скрыты (Navbar.css) и переезжают в меню
  // «Личный кабинет»: иначе с телефона в Отчёты или Журнал было не попасть вовсе.
  const closeMenu = () => setIsActive(false);
  const mobileSections = isMobile && (
    <div className="sections_account">
      {IS_STUDENT_CARD && (
        <button
          type="button" className="account_button_account account_menu_button"
          onClick={() => { closeMenu(); handleOpenDownloadGeneratedFiles(); }}
        >
          <SimCardDownloadRoundedIcon sx={{ fontSize: 19 }} />
          Шаблоны файлов
        </button>
      )}
      {SHOW_JOURNAL && <NavLink to={JOURNAL_GROUPS} className="account_button_account" onClick={closeMenu}>
        <MenuBookIcon sx={{ fontSize: 19 }} />
        Журнал
      </NavLink>}
      {SHOW_CONTINGENT && <NavLink to={STATS} className="account_button_account" onClick={closeMenu}>
        <AnalyticsIcon sx={{ fontSize: 19 }} />
        Отчеты
      </NavLink>}
      {SHOW_CONTINGENT && <NavLink to={EMAIL_LIST} className="account_button_account" onClick={closeMenu}>
        <EmailIcon sx={{ fontSize: 19 }} />
        Рассылки
      </NavLink>}
      {SHOW_CONTINGENT && <NavLink to={AGENT_LIST} className="account_button_account" onClick={closeMenu}>
        <GroupIcon sx={{ fontSize: 19 }} />
        Агенты
      </NavLink>}
    </div>
  );

  return (
    <section className="navbar">
      {IS_STUDENT_CARD && <div className="downloadGeneratedFiles">
        <Tooltip title="Автоматическое генерирование файлов" placement="top" className="navbar__item">
          <div onClick={handleOpenDownloadGeneratedFiles}>
            <SimCardDownloadRoundedIcon
              sx={{ fontSize: 20 }}
              className="navbar__item"
            />
            Шаблоны файлов
          </div>
        </Tooltip>
      </div>}
      {SHOW_JOURNAL && <NavLink to={JOURNAL_GROUPS} className="navbar__item"> <MenuBookIcon sx={{ fontSize: 20 }} />
        Журнал
      </NavLink>}
      {SHOW_CONTINGENT && <NavLink to={STATS} className="navbar__item"> <AnalyticsIcon sx={{ fontSize: 20 }} />
        Отчеты
      </NavLink>}
      {SHOW_CONTINGENT && <NavLink to={EMAIL_LIST} className="navbar__item"> <EmailIcon sx={{ fontSize: 20 }} />
        Рассылки
      </NavLink>}
      {SHOW_CONTINGENT && <NavLink to={AGENT_LIST} className="navbar__item"> <GroupIcon sx={{ fontSize: 20 }} />
        Агенты
      </NavLink>}
      <div className="account">
        <div className="dropdown_btn_account" onClick={() => setIsActive(!isActive)}>
          {IS_TEACHER_ACCESS
            ?
            <IconButton aria-label="Личный кабинет">
              <HomeIcon sx={{ fontSize: 18, color: "#FA7A45" }} />
            </IconButton>
            :
            <NavLink to={ACCOUNT_ROUTE}>
              <IconButton aria-label="Личный кабинет">
                <HomeIcon sx={{ fontSize: 18, color: "#FA7A45" }} />
              </IconButton>
            </NavLink>
          }
          Личный кабинет
        </div>

        {isActive && (
          <div className="dropdown_content_account">
            <div className="user_account">
              <div className="user_name_account">{userName}</div>
            </div>
            {mobileSections}
            {!IS_TEACHER_ACCESS && (
              <NavLink to={ACCOUNT_ROUTE} className="account_button_account">
                <PersonIcon sx={{ fontSize: 19 }} />
                Личный кабинет
              </NavLink>
            )}
            <button
              className="exit_button_account"
              onClick={() => {
                dropToken();
                window.location.reload();
              }}><ExitToAppIcon sx={{ fontSize: 18 }} />
              Выход
            </button>
          </div>
        )}
      </div>
      {
        isDownloadGeneratedFiles &&
        <Suspense fallback={<></>}>
          <ModalCustom active={isDownloadGeneratedFiles} setActive={setIsDownloadGeneratedFiles}>
            <DownloadGeneratedFilesModal />
          </ModalCustom>
        </Suspense>
      }
    </section>
  );
}


